import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Alert, AlertDescription } from './ui/alert'
import { Card, CardContent, CardHeader, CardTitle } from './ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { useCan } from '../contexts/PermissionsContext'
import {
  useContractProperties,
  useContractTypeProperties,
  useSetContractPropertyValue,
  useUpdateContract,
  type ContractPropertyValue,
} from '../hooks/useQueries'
import { useFormatters } from '../lib/format'
import { apiErrorDetail } from '../lib/apiError'
import type { components } from '../api/schema'

type Contract = components['schemas']['Contract']

export const LEASE_END_REASONS = ['term', 'tenant_notice', 'landlord_notice', 'mutual', 'court', 'abandonment'] as const

interface Props {
  org: string
  contract: Contract
  onOpenTab: (tab: string) => void
}

const NO_REASON = '__none__'

/**
 * Rent and term of a lease (spec 2026-10-25-02 §5). It reads the rent, the
 * charges and the frequency (written in the Clauses tab), and writes what the
 * lease page owns: the currency of the rent and the effective end with its
 * reason.
 */
export function LeaseTermsCard({ org, contract, onOpenTab }: Props) {
  const { t } = useTranslation('contracts')
  const { formatDate, formatMoney } = useFormatters()
  const canWrite = useCan('contracts.write')
  const contractUid = contract.uid as string
  const typeUid = (contract as any).contract_type?.uid || contract.contract_type_uid || ''

  const { data: values = [] } = useContractProperties(org, contractUid)
  const { data: typeProps = [] } = useContractTypeProperties(org, typeUid, { enabled: !!typeUid })
  const setClause = useSetContractPropertyValue(org, contractUid)
  const updateContract = useUpdateContract(org, contractUid)

  const valueOf = useMemo(() => {
    const byCode = new Map<string, unknown>()
    for (const v of values as ContractPropertyValue[]) {
      const code = (v.property_definition as { code?: string } | undefined)?.code
      if (code) byCode.set(code, v.value)
    }
    return (code: string) => byCode.get(code)
  }, [values])
  const currencyDefUid = useMemo(
    () => typeProps.find(p => (p.property_definition as { code?: string } | undefined)?.code === 'rent_currency')?.property_definition?.uid,
    [typeProps],
  )

  const storedCurrency = typeof valueOf('rent_currency') === 'string' ? (valueOf('rent_currency') as string) : ''
  const storedEndedOn = contract.ended_on ?? ''
  const storedReason = contract.end_reason ?? ''

  const [currency, setCurrency] = useState(storedCurrency)
  const [endedOn, setEndedOn] = useState(storedEndedOn)
  const [reason, setReason] = useState(storedReason)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  useEffect(() => setCurrency(storedCurrency), [storedCurrency])
  useEffect(() => setEndedOn(storedEndedOn), [storedEndedOn])
  useEffect(() => setReason(storedReason), [storedReason])

  const rentMinor = Number(valueOf('rent_amount_minor'))
  const chargesMinor = Number(valueOf('charges_amount_minor'))
  const frequency = typeof valueOf('rent_frequency') === 'string' ? (valueOf('rent_frequency') as string) : ''
  const shownCurrency = storedCurrency || null
  const end = contract.ended_on || contract.end_date
  const proposal = contract.proposed_rent_currency

  const currencyValid = currency === '' || /^[A-Z]{3}$/.test(currency)
  const dirty = currency !== storedCurrency || endedOn !== storedEndedOn || reason !== storedReason

  const saveCurrency = async (next: string) => {
    if (!currencyDefUid || next === storedCurrency) return
    await setClause.mutateAsync(currencyDefUid, next)
  }

  const save = async () => {
    setError(null)
    setSaving(true)
    try {
      await saveCurrency(currency)
      if (endedOn !== storedEndedOn || reason !== storedReason) {
        await updateContract.mutateAsync({
          ended_on: endedOn || null,
          end_reason: endedOn && reason ? (reason as Contract['end_reason']) : null,
        } as any)
      }
    } catch (err) {
      setError(apiErrorDetail(err, t('lease_terms.save_failed')))
    } finally {
      setSaving(false)
    }
  }

  const applyProposal = async () => {
    if (!proposal) return
    setError(null)
    setCurrency(proposal)
    try {
      await saveCurrency(proposal)
    } catch (err) {
      setError(apiErrorDetail(err, t('lease_terms.save_failed')))
    }
  }

  const frequencyLabel = frequency ? t(`lease_terms.frequency_${frequency}`, { defaultValue: frequency }) : '—'

  return (
    <Card data-testid="lease-terms-card" data-disabled={!canWrite}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between gap-2 text-base">
          {t('lease_terms.title')}
          <Button variant="link" size="sm" className="h-auto p-0" onClick={() => onOpenTab('clauses')} data-testid="lease-terms-edit-clauses">
            {t('lease_terms.edit_clauses')}
          </Button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
          <div className="space-y-1">
            <p className="text-xs uppercase text-muted-foreground">{t('lease_terms.rent')}</p>
            <p className="font-semibold" data-testid="lease-terms-rent">
              {Number.isFinite(rentMinor) && valueOf('rent_amount_minor') != null ? formatMoney(rentMinor, shownCurrency) : '—'}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs uppercase text-muted-foreground">{t('lease_terms.charges')}</p>
            <p className="font-semibold" data-testid="lease-terms-charges">
              {Number.isFinite(chargesMinor) && valueOf('charges_amount_minor') != null ? formatMoney(chargesMinor, shownCurrency) : '—'}
            </p>
          </div>
          <div className="space-y-1">
            <p className="text-xs uppercase text-muted-foreground">{t('lease_terms.frequency')}</p>
            <p className="font-semibold" data-testid="lease-terms-frequency">{frequencyLabel}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs uppercase text-muted-foreground">{t('lease_terms.start')}</p>
            <p className="font-semibold" data-testid="lease-terms-start">{contract.start_date ? formatDate(contract.start_date) : '—'}</p>
          </div>
          <div className="space-y-1">
            <p className="text-xs uppercase text-muted-foreground">{t('lease_terms.end')}</p>
            <p className="font-semibold" data-testid="lease-terms-term" data-ended={!!contract.ended_on}>
              {end ? formatDate(end) : t('lease_terms.open_ended')}
            </p>
            {contract.ended_on && contract.end_reason && (
              <p className="text-xs text-muted-foreground" data-testid="lease-terms-term-reason">
                {t(`lease_terms.reason_${contract.end_reason}`)}
              </p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          {currencyDefUid && (
            <div className="space-y-1">
              <Label htmlFor="lease-terms-currency">{t('lease_terms.currency')}</Label>
              <Input
                id="lease-terms-currency"
                value={currency}
                maxLength={3}
                placeholder="EUR"
                disabled={!canWrite}
                onChange={e => setCurrency(e.target.value.toUpperCase().trim())}
                aria-invalid={!currencyValid}
                data-testid="lease-terms-currency-input"
              />
              {!storedCurrency && proposal && canWrite && (
                <Button variant="outline" size="sm" onClick={applyProposal} data-testid="lease-terms-currency-proposal">
                  {t('lease_terms.use_currency', { currency: proposal })}
                </Button>
              )}
              {!currencyValid && <p className="text-xs text-destructive">{t('lease_terms.currency_invalid')}</p>}
            </div>
          )}
          <div className="space-y-1">
            <Label htmlFor="lease-terms-ended-on">{t('lease_terms.ended_on')}</Label>
            <Input
              id="lease-terms-ended-on"
              type="date"
              value={endedOn}
              disabled={!canWrite}
              onChange={e => {
                setEndedOn(e.target.value)
                if (!e.target.value) setReason('')
              }}
              data-testid="lease-terms-ended-on"
            />
          </div>
          <div className="space-y-1">
            <Label>{t('lease_terms.end_reason')}</Label>
            <Select
              value={reason || NO_REASON}
              onValueChange={v => setReason(v === NO_REASON ? '' : v)}
              disabled={!canWrite || !endedOn}
            >
              <SelectTrigger data-testid="lease-terms-end-reason">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_REASON} data-testid="lease-terms-end-reason-none">{t('lease_terms.reason_none')}</SelectItem>
                {LEASE_END_REASONS.map(r => (
                  <SelectItem key={r} value={r} data-testid={`lease-terms-end-reason-${r}`}>
                    {t(`lease_terms.reason_${r}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {error && (
          <Alert variant="destructive" data-testid="lease-terms-error">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="flex justify-end">
          <Button
            size="sm"
            onClick={save}
            disabled={!canWrite || !dirty || !currencyValid || saving}
            data-testid="lease-terms-save"
          >
            {t('lease_terms.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
